import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ChevronDown, X, Plus, Search, RefreshCcwIcon, CheckIcon } from "lucide-react";
import {
  getUsers,
  addManagerToHat,
  removeManagerFromHat,
  getHatManagers,
  apiErrorMessage
} from "../../../../service/apiService";
import { User } from "../../../../types";

interface Manager {
  user_id: string;
  username: string;
  email: string;
  name: string;
}

interface VAManagerSelectorProps {
  hatId?: string;
  isEnabled?: boolean;
  selectedManagers?: Manager[];
  onManagerAdded?: (manager: Manager) => void;
  onManagerRemoved?: (managerId: string) => void;
  isOwnerOrAdmin?: boolean;
  userRole?: string; // Add userRole prop to determine if user is regular
}

const VAManagerSelector: React.FC<VAManagerSelectorProps> = ({
  hatId,
  isEnabled = true,
  selectedManagers = [],
  onManagerAdded,
  onManagerRemoved,
  isOwnerOrAdmin = false,
  userRole = "user" // Default to regular user 
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [managers, setManagers] = useState<Manager[]>(selectedManagers);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [showAddManager, setShowAddManager] = useState(false);
  const [addingManagerId, setAddingManagerId] = useState<string | null>(null);
  const [removingManagerId, setRemovingManagerId] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState(""); // For regular users to input email

  // Determine if current user is a regular user
  const isRegularUser = userRole !== "admin";

  // Load existing managers for edit mode
  useEffect(() => {
    if (hatId && isEnabled) {
      loadManagers();
    }
  }, [hatId, isEnabled]);

  // Load users when "Add Manager" is clicked (only for admin/owner)
  useEffect(() => {
    if (showAddManager && users.length === 0 && !isRegularUser) {
      fetchUsers();
    }
  }, [showAddManager, isRegularUser]);

  // Update local managers when prop changes
  useEffect(() => {
    setManagers(selectedManagers);
  }, [selectedManagers]);

  const loadManagers = async () => {
    if (!hatId) return;
    
    setIsLoadingManagers(true);
    try {
      const response = await getHatManagers(hatId);
      if (response.data && response.data.managers) {
        setManagers(response.data.managers);
      }
    } catch (error) {
      console.error("Error loading managers:", error);
      toast.error("Failed to load managers");
    } finally {
      setIsLoadingManagers(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const result = await getUsers();
      // Filter out users who are already managers
      const availableUsers = result.filter(
        user => !managers.some(manager => manager.user_id === user.id)
      );
      setUsers(availableUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Filter users based on search query (only for admin/owner)
  const filteredUsers = useMemo(() => {
    return userQuery === ""
      ? users
      : users.filter(user =>
          user.id.toLowerCase().includes(userQuery.toLowerCase())
        );
  }, [users, userQuery]);

  // Function to handle adding manager for regular users via email input
  const handleAddManagerByEmail = async () => {
    const trimmedEmail = emailInput.trim();
    
    if (!trimmedEmail) {
      toast.error("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoadingUsers(true);
    try {
      // Fetch all users to check if email exists
      const allUsers = await getUsers();
      
      // Find user by email (assuming user.id is the email or you have an email field)
      const matchingUser = allUsers.find(
        user => user.id.toLowerCase() === trimmedEmail.toLowerCase()
      );

      if (!matchingUser) {
        toast.error("User not found. Please check the email address.");
        setIsLoadingUsers(false);
        return;
      }

      // Check if user is already a manager
      if (managers.some(manager => manager.user_id === matchingUser.id)) {
        toast.error("This user is already a manager");
        setIsLoadingUsers(false);
        return;
      }

      // Proceed to add manager
      await handleAddManager(matchingUser);
      
      // Clear input on success
      setEmailInput("");
      
    } catch (error) {
      console.error("Error checking user:", error);
      toast.error("Failed to verify user");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAddManager = async (user: User) => {
    
    if (!hatId) {
      // For new VA creation, just update local state
      const newManager: Manager = {
        user_id: user.id,
        username: user.id,
        email: '',
        name: user.id
      };
      
      setManagers(prev => [...prev, newManager]);
      if (onManagerAdded) {
        onManagerAdded(newManager);
      }
      
      // Remove from available users immediately (only for admin/owner view)
      if (!isRegularUser) {
        setUsers(prev => prev.filter(u => u.id !== user.id));
      }
      return;
    }

    setAddingManagerId(user.id);
    try {
      const result = await addManagerToHat(hatId, user.id);
      
      // Reload managers to get updated list
      await loadManagers();
      
      // Refresh users list to remove the newly added manager (only for admin/owner)
      if (!isRegularUser) {
        await fetchUsers();
      }
      
      toast.success(`${user.id} added as manager`);
      
      // Clear search if no more users (only for admin/owner)
      if (!isRegularUser && filteredUsers.length <= 1) {
        setShowAddManager(false);
        setUserQuery("");
      }
      
      // Close modal for regular users
      if (isRegularUser) {
        setShowAddManager(false);
      }
      
    } catch (error) {
      console.error("🔴 Error adding manager:", error);
      // Show what the API said -- a locked VA explains itself and tells the
      // admin to unlock it first, which a generic failure would hide.
      toast.error(apiErrorMessage(error, "Failed to add manager"));
    } finally {
      setAddingManagerId(null);
    }
  };

  const handleRemoveManager = async (managerId: string) => {
    console.log("🔵 handleRemoveManager called with:", { managerId, hatId });
    
    if (!hatId) {
      // For new VA creation, just update local state
      setManagers(prev => prev.filter(m => m.user_id !== managerId));
      if (onManagerRemoved) {
        onManagerRemoved(managerId);
      }
      
      // Refresh users list for new VA creation (only for admin/owner)
      if (!isRegularUser) {
        await fetchUsers();
      }
      return;
    }

    setRemovingManagerId(managerId);
    try {
      const result = await removeManagerFromHat(hatId, managerId);
      
      // Update local state
      setManagers(prev => prev.filter(m => m.user_id !== managerId));
      
      // Refresh users list to include the removed manager (only for admin/owner)
      if (!isRegularUser) {
        await fetchUsers();
      }
      
      toast.success("Manager removed");
    } catch (error) {
      console.error("🔴 Error removing manager:", error);
      toast.error(apiErrorMessage(error, "Failed to remove manager"));
    } finally {
      setRemovingManagerId(null);
    }
  };

  // Refresh users list when managers change (only for admin/owner)
  useEffect(() => {
    if (showAddManager && !isRegularUser) {
      fetchUsers();
    }
  }, [managers, isRegularUser]);

  const canManage = isOwnerOrAdmin && isEnabled;

  return (
    <div className="relative">
      {/* Add Manager Button */}
      {canManage && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowAddManager(true)}
            className="flex items-center text-violet-600 hover:text-violet-700 font-medium text-sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Manager
          </button>
        </div>
      )}

      {/* Modal Overlay for Add Manager */}
      {showAddManager && (
        <div className="fixed inset-0 scrim flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 h-[500px] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add Manager</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddManager(false);
                  setUserQuery("");
                  setEmailInput("");
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-4 overflow-hidden flex flex-col min-h-0">
              
              {/* Regular User View - Email Input */}
              {isRegularUser ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Enter the full email address of the user you want to add as a manager.
                  </div>
                  
                  {/* Email Input Field */}
                  <div className="relative">
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      placeholder="Enter user email address..."
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddManagerByEmail();
                        }
                      }}
                    />
                  </div>

                  {/* Add Button */}
                  <button
                    type="button"
                    onClick={handleAddManagerByEmail}
                    disabled={isLoadingUsers || !emailInput.trim()}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-lg hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoadingUsers ? (
                      <>
                        <RefreshCcwIcon className="animate-spin h-4 w-4 mr-2" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Add Manager
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Admin/Owner View - User List */
                <>
                  {/* Search Input */}
                  <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      placeholder="Search users to add as manager..."
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                    />
                    {isLoadingUsers && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Users List */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    {isLoadingUsers ? (
                      <div className="flex justify-center items-center h-64">
                        <RefreshCcwIcon className="animate-spin h-8 w-8 text-gray-400" />
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <div className="text-gray-500">
                            {userQuery ? "No users found matching your search" : "No available users to add as managers"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredUsers.map(user => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.id}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddManager(user)}
                              disabled={addingManagerId === user.id}
                              className="ml-3 flex items-center justify-center w-8 h-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {addingManagerId === user.id ? (
                                <RefreshCcwIcon className="animate-spin h-4 w-4" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddManager(false);
                  setUserQuery("");
                  setEmailInput("");
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Managers List */}
      <div className="space-y-2">
        {isLoadingManagers ? (
          <div className="flex justify-center py-2">
            <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
          </div>
        ) : managers.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-2">
            {canManage ? "No managers assigned" : "No managers"}
          </div>
        ) : (
          managers.map(manager => (
            <div 
              key={manager.user_id}
              className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="w-2 h-2 rounded-full bg-violet-600 flex-shrink-0"></div>
                <span className="text-sm text-gray-700 truncate">
                  {manager.user_id}
                </span>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => handleRemoveManager(manager.user_id)}
                  disabled={removingManagerId === manager.user_id}
                  className="ml-2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  {removingManagerId === manager.user_id ? (
                    <RefreshCcwIcon className="animate-spin h-3 w-3" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Disabled state message */}
      {!isEnabled && (
        <div className="text-xs text-gray-500 mt-2">
          Manager functionality is not enabled for this VA
        </div>
      )}
    </div>
  );
};

export default VAManagerSelector;